import { boxFixedPriceCentsOf, tt } from '@agos/shared'

/**
 * The burger box: a set of slots, each filled from one category, sold as one thing.
 *
 * **Measured: `box_legacy` is active on this data** — four slots (burger, príloha, dressing, nápoje),
 * `triggerCategoryId` 3, and every slot `required: false`. So a customer can add an empty-ish box, and
 * the emptiness rule below is the one that matters rather than the required-ness.
 *
 * FB-190 is the manager's version of the same feature and its lesson carries: legacy checks each slot's
 * own `required` flag before checking whether the box has anything in it at all, so a box of four
 * optional slots could be added completely empty — a line on the order, at some price, containing
 * nothing. **The empty check comes first here.**
 *
 * A box is priced as the sum of what is in it. That is not the kombo's rule and the difference is real:
 * a kombo is three pizzas *instead of* one, a box is four items *together*.
 */

export interface BoxSlot {
  id: string
  label: string
  categoryId: number | null
  required: boolean
  /**
   * Pinned into the slot and in the box whether or not the customer chooses - `presetItems[].fixed`.
   *
   * So it fills the slot: a `required` slot carrying one needs nothing chosen, and a box made only of
   * these is not empty.
   */
  fixedProductIds: number[]
  /**
   * The slot's **offer**, instead of everything in its category - `presetItems` with `fixed: false`.
   *
   * **FB-805: these two were one list, and the wrong one.** Every entry was added to the basket
   * automatically, which is `fixed: true`'s rule applied to both kinds - so the owner's student box,
   * whose burger slot offers a choice of two, would have put both of them in. An offer is not a
   * filling: this narrows what may be picked and becomes a line only when it is.
   */
  presetProductIds: number[]
}

export interface BoxConfig {
  id: string
  name: string
  description: string
  active: boolean
  /** The category whose page offers the box. `null` means it is offered nowhere. */
  triggerCategoryId: number | null
  hideProductImages: boolean
  /**
   * Whether the box opens collapsed - §9.646, owner O37.
   *
   * **Absent means collapsed**, which is the opposite of `hideProductImages` above and is the owner's own
   * instruction: *"and make it collapsed by default"*. So a franchise that has never touched the setting gets a
   * collapsed box, and the only way to have it open is to say so.
   *
   * A **default**, not a state: it decides how the box opens, and a customer who expands it keeps it open while
   * they are using it.
   */
  collapsedByDefault: boolean
  /**
   * Sold for this, whatever its parts cost - §10A.107, the owner's item E.
   *
   * In **cents**, because everything the basket prices is in cents (FB-056). `null` means *sum of
   * the parts*, which is every box today; a stored zero is read as `null`, because in a free-form
   * blob they cannot be told apart and a free box is a gift rule.
   *
   * **The server is the authority.** `ServerPricing` applies the same split from the same stored
   * configuration; this is here so the screen shows what the bill will say, not so the client can
   * decide a price.
   */
  fixedPriceCents: number | null
  slots: BoxSlot[]
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function id(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * One kind of preset item - FB-805, and the same reader the till has had all along.
 *
 * **The wire shape is `{ productId, fixed }`, not a number.** This read `presetItems.map(id)`, so every
 * pinned item parsed to `null` and both lists were empty on every box the owner has. The measurement
 * that hid it is in this file's own javadoc: `box_legacy`'s four slots all carry `presetItems: []`, and
 * a map over an empty list is empty however wrong the mapper is.
 *
 * `fixed` absent counts as `false`, which is what the owner's rows carry and the safer half: an item
 * offered and not chosen costs nothing, an item added unasked appears on a bill.
 */
function pinned(presets: unknown, fixed: boolean): number[] {
  if (!Array.isArray(presets)) return []
  return presets.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    if ((row.fixed === true) !== fixed) return []
    const productId = id(row.productId)
    return productId === null ? [] : [productId]
  })
}

export function boxConfigsOf(payload: unknown): BoxConfig[] {
  if (!Array.isArray(payload)) return []
  const configs: BoxConfig[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const slots = Array.isArray(row.slots) ? row.slots : []
    configs.push({
      id: text(row.id),
      name: text(row.name),
      description: text(row.description),
      active: row.active === true,
      triggerCategoryId: id(row.triggerCategoryId),
      hideProductImages: row.hideProductImages === true,
      // §9.646 - `!== false`, not `=== true`: absence means collapsed. The asymmetry with the line above is
      // deliberate and is the owner's instruction; stated here because the next display boolean will be copied
      // from whichever of the two somebody happens to read.
      collapsedByDefault: row.collapsedByDefault !== false,
      fixedPriceCents: boxFixedPriceCentsOf(row.fixedPrice),
      slots: slots
        .filter((slot): slot is Record<string, unknown> => typeof slot === 'object' && slot !== null)
        .map((slot) => ({
          id: text(slot.id),
          label: text(slot.label),
          categoryId: id(slot.categoryId),
          required: slot.required === true,
          fixedProductIds: pinned(slot.presetItems, true),
          presetProductIds: pinned(slot.presetItems, false),
        }))
        // A slot with no id cannot be filled or removed; it is a heading with a dropdown under it.
        .filter((slot) => slot.id !== ''),
    })
  }
  return configs
}

/** The box a category offers, or `null` — only one, and only when it is switched on. */
/**
 * Every box this category offers — §9.200.
 *
 * **`boxFor` below ends in `.find`, and that was the whole of the owner's report.** The payload has
 * always been an array, `boxConfigsOf` has always parsed all of it, and a second active box on the
 * same category was fetched, parsed, held and never drawn — configurable, savable, invisible.
 *
 * In the server's order. A box with no slots is not offered: an empty panel is a control that
 * cannot be completed.
 */
export function boxesFor(
  configs: readonly BoxConfig[],
  categoryId: number | null,
): BoxConfig[] {
  if (categoryId === null) return []
  return configs.filter(
    (config) => config.active && config.triggerCategoryId === categoryId && config.slots.length > 0,
  )
}

/** The first of them, for a caller that wants one answer. */
export function boxFor(configs: readonly BoxConfig[], categoryId: number | null): BoxConfig | null {
  return boxesFor(configs, categoryId)[0] ?? null
}

/**
 * What a slot may be filled with: **its preset items where it names any, otherwise the products in its
 * category** - legacy's rule, and `choicesForSlot`'s at the till.
 *
 * **FB-805: the narrowing was missing entirely**, and the owner's student box is what it costs. Its four
 * slots all name *Gratis veci* and are told apart **only** by their presets, so without this every slot
 * offered the same five products - a *Cheese burger* could fill the *Nápoj* slot, and a *Dresing Zdarma*
 * could satisfy a burger slot marked `required`.
 *
 * **The availability filters below apply to a narrowed list too**, which is not a detail: §9.155's rule
 * is that a slot must not offer what the kitchen has run out of, and that is as true of an item the
 * operator pinned as of one the category happens to hold. The server refuses the line either way
 * (§10A.77), so the alternative is a customer building the whole box and being turned down at checkout.
 */
export function optionsFor<
  T extends {
    id: number
    categoryId?: number
    active?: boolean
    visibleOnWeb?: boolean
    orderable?: boolean
  },
>(slot: BoxSlot, products: readonly T[]): T[] {
  const offered =
    slot.presetProductIds.length > 0
      ? products.filter((product) => slot.presetProductIds.includes(product.id))
      : slot.categoryId === null
        ? []
        : products.filter((product) => product.categoryId === slot.categoryId)
  return offered.filter(
    (product) =>
      product.active !== false &&
      product.visibleOnWeb !== false &&
      // §9.155 — a slot must not offer what the kitchen has run out of. The card for that product is
      // greyed on the menu, but a box slot builds its own list and would have offered it happily;
      // the server refuses the line (§10A.77), so without this the customer picks it, builds the
      // whole box, and is refused at checkout. Same late refusal §9.156 removed from the manager.
      product.orderable !== false,
  )
}

/**
 * The items a slot is filled with regardless of what the customer chooses - FB-850, §9.563.
 *
 * **`optionsFor`'s sibling.** That answers *what may this slot be filled with*; this answers *what is already in
 * it*, and the pair lives in one file so the two readings of `presetItems` cannot drift. `boxLines` already puts
 * these in the basket and `boxProblem` already counts a slot holding one as satisfied - what was missing is the
 * customer being told, which legacy does in the slot row (`Home.tsx`: `fixedProds.map(p => p.name).join(", ")`,
 * styled as filled).
 *
 * **Resolved through the priced product list rather than trusting the id.** A pin can name a product that has
 * since been deleted or switched off, and an id with no sellable product behind it is dropped - otherwise the
 * panel promises an item the kitchen cannot make, which is the late refusal §9.155 exists to prevent. The
 * filter is `optionsFor`'s, for the same reason.
 */
export function fixedItemsFor<
  T extends {
    id: number
    active?: boolean
    visibleOnWeb?: boolean
    orderable?: boolean
  },
>(slot: BoxSlot, products: readonly T[]): T[] {
  return slot.fixedProductIds.flatMap((productId) => {
    const product = products.find((candidate) => candidate.id === productId)
    if (product === undefined) return []
    if (product.active === false || product.visibleOnWeb === false || product.orderable === false) {
      return []
    }
    return [product]
  })
}

export type BoxSelection = Readonly<Record<string, number | null>>

export const EMPTY_BOX: BoxSelection = {}

/**
 * Why the box cannot be added yet, or `''`.
 *
 * **Empty first.** FB-190: legacy checks each slot's `required` flag and, with every slot optional —
 * which is the measured configuration — lets a box with nothing in it onto the order.
 *
 * **FB-805 - only a `fixed` item fills a slot.** Both rules below asked about every preset, which counts
 * a slot's *offer* as though something were already in it: the owner's student box would have been
 * addable with nothing chosen, and its `required` burger slot satisfied by an offer of two the customer
 * never picked between. `fixedForSlot` is the till's spelling of the same distinction.
 */
export function boxProblem(config: BoxConfig, selection: BoxSelection): string {
  const chosen = config.slots.filter((slot) => (selection[slot.id] ?? null) !== null)
  const pinnedIn = config.slots.filter((slot) => slot.fixedProductIds.length > 0)
  if (chosen.length === 0 && pinnedIn.length === 0) return tt('Do boxu zatiaľ nič nie je vybrané.')

  const missing = config.slots.filter(
    (slot) =>
      slot.required &&
      (selection[slot.id] ?? null) === null &&
      slot.fixedProductIds.length === 0,
  )
  if (missing.length > 0) {
    return `${tt('Chýba')}: ${missing.map((slot) => slot.label).join(', ')}`
  }
  return ''
}

export interface BoxLine {
  productId: number
  slotLabel: string
}

/**
 * What goes in the basket: one line per item, all sharing a group id.
 *
 * Not one composed line, because a box is real products — the kitchen makes a burger and a portion of
 * fries, and the order has to name both. The group id is what lets the basket remove them as one thing,
 * which is `removeBox`'s whole reason for existing.
 *
 * **FB-805 - the fixed items, not every preset.** This added each slot's whole preset list, so a slot
 * offering a choice of two put *both* in the basket. Fixed first and then the chosen one, which is the
 * order the till builds them in, so the two screens list a box the same way round.
 */
export function boxLines(config: BoxConfig, selection: BoxSelection): BoxLine[] {
  const lines: BoxLine[] = []
  for (const slot of config.slots) {
    for (const fixed of slot.fixedProductIds) {
      lines.push({ productId: fixed, slotLabel: slot.label })
    }
    const chosen = selection[slot.id] ?? null
    if (chosen !== null) lines.push({ productId: chosen, slotLabel: slot.label })
  }
  return lines
}

/**
 * The group id for one box in the basket.
 *
 * Derived from the box and what is in it, so adding the same box twice makes one line of two rather
 * than two groups that look identical and cannot be told apart — the same reasoning as FB-C25's kombo,
 * and the same mistake avoided.
 */
export function boxGroupId(config: BoxConfig, selection: BoxSelection): string {
  const filling = config.slots
    .map((slot) => `${slot.id}=${selection[slot.id] ?? ''}`)
    .join(',')
  return `${config.id}:${filling}`
}
